/**
 * « Demander à l'agent » — le panneau du patron.
 *
 * L'agent lit, fait les écritures internes que le patron lui a ouvertes, et
 * **prépare** ce qui part chez un client ou touche à l'argent. Ces demandes
 * s'affichent ici, sous sa réponse, avec le résumé écrit par le code et les
 * deux boutons de toujours : on valide sans quitter la conversation.
 *
 * Les boutons sont ceux du tableau de bord (`useApprovePendingAction`) : une
 * demande préparée ici est la même ligne de `pending_actions` que celle qu'un
 * conducteur soumet. Elle y apparaît aussi, et ne peut donc pas se perdre si
 * le panneau se ferme.
 */
import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { Bot, Check, CornerDownLeft, RotateCcw, X } from "lucide-react";
import type { AgentProposal, PendingAction } from "@dker/core";
import { Dialog, DialogCloseButton } from "@/components/shared/Dialog";
import { Button } from "@/components/forms/Button";
import { Dictee } from "@/components/forms/Dictee";
import { useAskAgent } from "@/hooks/useAgent";
import {
  useApprovePendingAction,
  usePendingActions,
  useRejectPendingAction,
} from "@/hooks/usePendingActions";
import { useAgentStore } from "@/stores/agentStore";
import type { AgentTurn } from "@/stores/agentStore";
import { cn } from "@/lib/utils";

/** Ce que l'agent a fait, dit en français plutôt qu'en nom de verbe. */
const FAIT: Record<string, string> = {
  affecter_au_chantier: "Affectation posée",
  retirer_affectation: "Affectation retirée",
  enregistrer_rendez_vous: "Rendez-vous noté",
  supprimer_rendez_vous: "Rendez-vous supprimé",
  changer_statut_chantier: "Statut du chantier changé",
  valider_avancement_declare: "Avancement validé",
  traiter_signalement: "Signalement traité",
  enregistrer_echeance: "Échéance enregistrée",
  reporter_echeance: "Échéance reportée",
  noter_quart_heure_securite: "Quart d'heure sécurité noté",
  changer_statut_devis: "Statut du devis changé",
  enregistrer_client: "Fiche client enregistrée",
};

const EXEMPLES = [
  "Qui me doit de l'argent ?",
  "Quels chantiers dérivent ?",
  "Qui est sur quel chantier demain ?",
  "Relance les factures en retard",
];

export function AgentPanel() {
  const open = useAgentStore((s) => s.open);
  const setOpen = useAgentStore((s) => s.setOpen);
  if (!open) return null;
  return <Panneau onClose={() => setOpen(false)} />;
}

function Panneau({ onClose }: { onClose: () => void }) {
  const turns = useAgentStore((s) => s.turns);
  const reset = useAgentStore((s) => s.reset);
  const ask = useAskAgent();
  const [texte, setTexte] = useState("");
  const fin = useRef<HTMLDivElement>(null);
  const champ = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fin.current?.scrollIntoView({ block: "end" });
  }, [turns.length, ask.isPending]);

  const envoyer = (question = texte) => {
    const q = question.trim();
    if (q.length < 3 || ask.isPending) return;
    setTexte("");
    ask.mutate(q);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Entrée envoie, Maj+Entrée va à la ligne : l'usage de toutes les
    // messageries, donc celui qu'on n'a pas à apprendre.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      envoyer();
    }
  };

  return (
    <Dialog
      onClose={onClose}
      labelledBy="agent-title"
      className="sm:max-w-2xl h-[88vh] sm:h-[80vh] flex flex-col"
    >
      <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
        <div className="min-w-0">
          <h2 id="agent-title" className="flex items-center gap-2 text-lg font-semibold text-text-primary">
            <Bot size={20} className="text-accent-ink" />
            Demander à l’agent
          </h2>
          <p className="text-[12.5px] text-text-muted mt-0.5">
            Il prépare, vous validez : rien ne part chez un client sans vous.
          </p>
        </div>
        <div className="flex items-center gap-1">
          {turns.length > 0 && (
            <Button size="sm" variant="ghost" onClick={reset} disabled={ask.isPending}>
              <RotateCcw size={14} />
              Nouvelle
            </Button>
          )}
          <DialogCloseButton />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4 space-y-4" aria-live="polite">
        {turns.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">
              Posez une question sur l’activité, ou dites ce qu’il faut faire.
            </p>
            <div className="flex flex-wrap gap-2">
              {EXEMPLES.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => envoyer(q)}
                  className="px-3 py-1.5 rounded-full border border-border text-sm text-text-secondary hover:text-text-primary hover:bg-bg-card-hover touch-target"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          turns.map((t) => <Tour key={t.id} tour={t} />)
        )}
        {ask.isPending && (
          <p className="text-sm text-text-muted flex items-center gap-2">
            <Bot size={16} className="animate-pulse" />
            L’agent regarde…
          </p>
        )}
        <div ref={fin} />
      </div>

      <div className="pt-3 border-t border-border space-y-2">
        <div className="flex items-end gap-2">
          <textarea
            ref={champ}
            autoFocus
            rows={2}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ex. Mets Momo sur la chaufferie mardi"
            aria-label="Votre question"
            className="flex-1 resize-none rounded-xl bg-bg-input border border-border px-3 py-2 text-[15px] text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <Button
            onClick={() => envoyer()}
            loading={ask.isPending}
            disabled={texte.trim().length < 3}
            aria-label="Envoyer"
          >
            <CornerDownLeft size={16} />
          </Button>
        </div>
        <Dictee
          onTexte={(phrase) => {
            setTexte((t) => (t.trim() ? `${t.trim()} ${phrase}` : phrase));
            champ.current?.focus();
          }}
        />
      </div>
    </Dialog>
  );
}

function Tour({ tour }: { tour: AgentTurn }) {
  if (tour.role === "patron") {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] rounded-2xl rounded-br-md bg-accent/15 px-3.5 py-2 text-[15px] text-text-primary whitespace-pre-line">
          {tour.text}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-w-[92%]">
      <p
        className={cn(
          "text-[15px] leading-relaxed whitespace-pre-line",
          tour.failed ? "text-danger-ink" : "text-text-primary",
        )}
      >
        {tour.text}
      </p>
      {tour.writes && tour.writes.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {tour.writes.map((w, i) => (
            <li
              key={`${w}-${i}`}
              className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-[12px] text-success-ink"
            >
              <Check size={12} />
              {FAIT[w] ?? w}
            </li>
          ))}
        </ul>
      )}
      {tour.proposals && tour.proposals.length > 0 && (
        <div className="space-y-2">
          {tour.proposals.map((p) => (
            <Demande key={p.pendingActionId} proposal={p} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Une demande préparée par l'agent.
 *
 * Son état se lit dans la liste des demandes en attente, pas dans la
 * conversation : validée depuis le tableau de bord, elle doit aussi
 * apparaître traitée ici.
 */
function Demande({ proposal }: { proposal: AgentProposal }) {
  const { data, isLoading } = usePendingActions();
  const approuver = useApprovePendingAction();
  const refuser = useRejectPendingAction();
  const pending: PendingAction | undefined = data?.find((a) => a.id === proposal.pendingActionId);
  const occupe = approuver.isPending || refuser.isPending;

  return (
    <div className="rounded-xl border-2 border-accent/60 bg-bg-card px-3.5 py-3">
      {/* Le résumé vient de `pending_actions`, écrit par le code à partir de
          la base : l'agent ne tient pas la plume de ce qu'on valide. */}
      <p className="text-sm text-text-primary leading-relaxed">
        {pending?.summary ?? proposal.summary}
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {pending ? (
          <>
            <Button size="sm" disabled={occupe} loading={approuver.isPending} onClick={() => approuver.mutate(pending)}>
              <Check size={14} />
              {pending.status === "confirmee" ? "Réessayer" : "Valider"}
            </Button>
            <Button size="sm" variant="ghost" disabled={occupe} onClick={() => refuser.mutate(pending.id)}>
              <X size={14} />
              Refuser
            </Button>
          </>
        ) : (
          <span className="text-[12.5px] text-text-muted">
            {isLoading ? "…" : "Traitée — elle n’attend plus rien."}
          </span>
        )}
      </div>
    </div>
  );
}
